/**
 * NotificationPreview Component
 * Real-time preview of notification appearance across different channels
 */

import clsx from 'clsx';
import type { Channel, NotificationContent } from '../types';

// Icon mapping for common icon names
const ICON_MAP: Record<string, string> = {
  sparkles: '✨',
  heart: '❤️',
  'information-circle': 'ℹ️',
  megaphone: '📢',
  bookmark: '🔖',
  star: '⭐',
  bell: '🔔',
  gift: '🎁',
  calendar: '📅',
  chat: '💬',
};

interface NotificationPreviewProps {
  channels: Channel[];
  content: NotificationContent;
  className?: string;
}

export function NotificationPreview({ channels, content, className }: NotificationPreviewProps) {
  const hasContent = content.title || content.body;
  const iconEmoji = content.icon ? (ICON_MAP[content.icon] || content.icon) : '🔮';

  if (channels.length === 0 && !hasContent) {
    return (
      <div className={clsx('p-4 rounded-lg bg-gray-50 text-center', className)}>
        <p className="text-sm text-gray-500">
          选择渠道并填写内容后可预览效果
        </p>
      </div>
    );
  }

  return (
    <div className={clsx('space-y-4', className)}>
      <h4 className="text-sm font-medium text-gray-700">预览效果</h4>

      <div className="space-y-4">
        {/* Push Preview - iOS Style */}
        {channels.includes('push') && (
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-wide text-gray-400">Push 通知 (iOS)</p>
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 max-w-[340px]">
              <div className="flex items-start gap-3">
                {/* App Icon */}
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xl shrink-0 shadow-sm">
                  🔮
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                      DORA
                    </span>
                    <span className="text-[11px] text-gray-400">
                      现在
                    </span>
                  </div>

                  {content.title && (
                    <h5 className="text-sm font-semibold text-gray-900 truncate">
                      {content.title}
                    </h5>
                  )}

                  <p className="text-sm text-gray-600 line-clamp-2">
                    {content.body || '通知内容...'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Realtime Preview - NotificationBar Style */}
        {channels.includes('realtime') && (
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-wide text-gray-400">应用内通知 (NotificationBar)</p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 max-w-[340px] flex items-center gap-3">
              {/* Icon */}
              <span className="text-xl shrink-0">{iconEmoji}</span>

              {/* Message */}
              <span className="text-sm text-gray-700 flex-1">
                {content.body || '通知内容...'}
              </span>

              {/* Action Button */}
              {content.action && (
                <span className="text-sm text-green-600 font-medium underline cursor-pointer shrink-0">
                  {content.action_label || '查看'}
                </span>
              )}

              {/* Close Button */}
              <span className="text-gray-400 cursor-pointer shrink-0 hover:text-gray-600">
                ✕
              </span>
            </div>
          </div>
        )}

        {/* Silent Push Preview */}
        {channels.includes('push_silent') && (
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-wide text-gray-400">静默推送</p>
            <div className="bg-gray-100 rounded-lg p-4 max-w-[340px]">
              <div className="flex items-center gap-2 text-gray-500">
                <span className="text-lg">📡</span>
                <div>
                  <p className="text-sm font-medium text-gray-600">后台静默更新</p>
                  <p className="text-xs text-gray-500">
                    此通知为静默推送，用户不会看到任何界面，数据将在后台更新
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Compact preview for list views
interface NotificationPreviewCompactProps {
  channels: Channel[];
  content: NotificationContent;
  className?: string;
}

export function NotificationPreviewCompact({ channels, content, className }: NotificationPreviewCompactProps) {
  const iconEmoji = content.icon ? (ICON_MAP[content.icon] || content.icon) : '';

  return (
    <div className={clsx('flex flex-col gap-1', className)}>
      {/* Channel badges */}
      <div className="flex flex-wrap gap-1">
        {channels.map(channel => (
          <span
            key={channel}
            className={clsx(
              'inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full text-white',
              channel === 'push' && 'bg-indigo-500',
              channel === 'push_silent' && 'bg-gray-500',
              channel === 'realtime' && 'bg-green-500'
            )}
          >
            {channel === 'push' && 'Push'}
            {channel === 'push_silent' && '静默'}
            {channel === 'realtime' && '应用内'}
          </span>
        ))}
      </div>

      {/* Content preview */}
      <div className="text-xs text-gray-600">
        {content.title && (
          <span className="font-medium text-gray-900">{content.title}</span>
        )}
        {content.title && content.body && <span className="mx-1">·</span>}
        {content.body && (
          <span className="line-clamp-1">{content.body}</span>
        )}
      </div>

      {/* Action indicator */}
      {content.action && (
        <div className="text-[10px] text-indigo-600">
          {iconEmoji} 跳转: {content.action}
        </div>
      )}
    </div>
  );
}
